import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import './PrivacyPolicy.css';

// Privacy Policy for game.livingchess.net. Kept in sync (in wording) with the
// website's Privacy page. Publicly accessible — no authentication required.
const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="privacy-container">
      <Header showUserSection={false} />
      <main className="privacy-content">
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-updated">Last updated: 13 June 2026</p>

        <section className="privacy-section">
          <h2>1. Who we are</h2>
          <p>
            Living Chess Inc. (&ldquo;Living Chess&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates the
            collaborative chess game at <strong>game.livingchess.net</strong> and the website at{' '}
            <strong>livingchess.net</strong>. We are the data controller responsible for your personal data.
          </p>
          <p>
            For any privacy question or to exercise your rights, contact us at{' '}
            <a href="mailto:azad.heydarov@livingchess.net">azad.heydarov@livingchess.net</a>.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. What data we collect</h2>
          <p>To create and manage your account, we collect and store:</p>
          <ul>
            <li>Your <strong>email address</strong>;</li>
            <li>A display name / nickname you choose;</li>
            <li>A Firebase-generated <strong>user ID</strong> and authentication tokens;</li>
            <li>Account timestamps (e.g. when your account was created);</li>
            <li>Basic game-participation data tied to games you join (e.g. your nickname and which team you played on);</li>
            <li><strong>Chat messages</strong> you send in a game&rsquo;s team chat.</li>
          </ul>
          <p>
            Your password is handled by <strong>Firebase Authentication</strong> and stored securely using
            industry-standard hashing. We never see or store your password in plain text.
          </p>
          <p>We do not collect special categories of data, and we do not use advertising or tracking cookies.</p>
        </section>

        <section className="privacy-section">
          <h2>3. Why we use it and our legal basis</h2>
          <p>
            We use this data to authenticate you, manage your account, and let you take part in games. Our legal
            basis under the GDPR is <strong>performance of a contract</strong> (Article 6(1)(b)) — providing the
            service you signed up for — and our <strong>legitimate interest</strong> (Article 6(1)(f)) in keeping
            the service secure and functional.
          </p>
        </section>

        <section className="privacy-section">
          <h2>4. Who we share it with</h2>
          <p>
            We use <strong>Google Firebase</strong> (provided by Google LLC, and Google Ireland Ltd. for users in
            the EU) for authentication and data storage. Google acts as our data processor and processes your data
            only on our instructions. We do not sell your data or share it with advertisers.
          </p>
          <p>
            Some games include <strong>AI players</strong>. In those games, the team chat messages and the current
            game state are sent to <strong>OpenAI</strong> (OpenAI, L.L.C.) so it can generate the AI players&rsquo;
            moves and messages. OpenAI processes this data as our sub-processor solely to return a response; under
            OpenAI&rsquo;s API terms, data submitted through the API is not used to train its models. Please avoid
            sharing sensitive personal information in chat during games that include AI players.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. International data transfers</h2>
          <p>
            Firebase and OpenAI may store and process data on servers located outside the European Economic Area,
            including in the United States. Where data is transferred outside the EEA, it is protected by the
            European Commission&rsquo;s <strong>Standard Contractual Clauses</strong>, which these providers include
            in their data processing terms.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Cookies and session storage</h2>
          <p>
            We use only what is strictly necessary to keep you signed in. Firebase Authentication stores session
            tokens in your browser (via local storage and/or cookies) so you don&rsquo;t have to log in on every
            visit. These are essential to the service and are not used for tracking or advertising.
          </p>
        </section>

        <section className="privacy-section">
          <h2>7. How long we keep your data</h2>
          <p>
            We keep your account data for as long as your account exists. When you delete your account, your
            Firebase Authentication record and your profile are removed. You can delete your account at any time
            from your dashboard (&ldquo;Delete my account&rdquo;). Chat messages and game records you contributed
            during play may remain as part of those games&rsquo; history; contact us if you would like those
            removed as well.
          </p>
        </section>

        <section className="privacy-section">
          <h2>8. Your rights</h2>
          <p>Under the GDPR you have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you;</li>
            <li>Rectify inaccurate or incomplete data;</li>
            <li>Erase your data (&ldquo;right to be forgotten&rdquo;);</li>
            <li>Restrict or object to our processing;</li>
            <li>Receive your data in a portable format;</li>
            <li>Lodge a complaint with your local data protection authority.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>9. How to exercise your rights</h2>
          <p>
            You can delete your account yourself at any time from your dashboard. For any other request, email us at{' '}
            <a href="mailto:azad.heydarov@livingchess.net">azad.heydarov@livingchess.net</a> and we will respond
            within the time limits required by law.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. When we do, we will revise the &ldquo;Last updated&rdquo;
            date above. Material changes will be communicated where appropriate.
          </p>
        </section>

        <button className="privacy-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
